import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SidebarCol } from "../../chatMain/Sidebar/Sidebar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faLock,
  faVideo,
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faCalendar,
  faUser,
  faArrowLeft,
  faShieldHalved,
} from "@fortawesome/free-solid-svg-icons";
import {
  getLiveEventById,
  getSecureJoinUrl,
} from "../../../services/liveEventsService";
import "./LiveJoinRedirect.css";

function parseUtcDate(rawDate) {
  if (!rawDate) return null;
  if (rawDate instanceof Date) return rawDate;
  let str = String(rawDate).trim();
  if (!str.includes("Z") && !str.includes("+") && !str.match(/-\d{2}:\d{2}$/)) {
    str = str.replace(" ", "T") + "Z";
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? new Date(rawDate) : d;
}

function formatDualTime(rawDate, durationMinutes = 30) {
  if (!rawDate) {
    return {
      localDateStr: "",
      localTimeStr: "",
      localTz: "",
      hostDateStr: "",
      hostTimeStr: "",
      isDifferentTz: false,
    };
  }

  const d = parseUtcDate(rawDate);
  const endD = new Date(d.getTime() + (durationMinutes || 30) * 60 * 1000);
  const userTz = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  const hostTz = "America/Toronto";

  const userFmtDate = new Intl.DateTimeFormat("en-US", {
    timeZone: userTz,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const userFmtTime = new Intl.DateTimeFormat("en-US", {
    timeZone: userTz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const hostFmtDate = new Intl.DateTimeFormat("en-US", {
    timeZone: hostTz,
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const hostFmtTime = new Intl.DateTimeFormat("en-US", {
    timeZone: hostTz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  const isDifferentTz =
    userTz !== hostTz &&
    userTz !== "America/New_York" &&
    userTz !== "EST" &&
    userTz !== "EDT";

  return {
    localDateStr: userFmtDate.format(d),
    localTimeStr: `${userFmtTime.format(d)} - ${userFmtTime.format(endD)}`,
    localTz: userTz,
    hostDateStr: hostFmtDate.format(d),
    hostTimeStr: `${hostFmtTime.format(d)} - ${hostFmtTime.format(endD)} EST`,
    isDifferentTz,
  };
}

export const LiveJoinRedirect = () => {
  const { ID } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("checking"); // 'checking' | 'too_early' | 'redirecting' | 'unauthorized' | 'ended' | 'error'
  const [eventData, setEventData] = useState(null);
  const [joinData, setJoinData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [nowTimestamp, setNowTimestamp] = useState(Date.now());
  const [redirectCountdown, setRedirectCountdown] = useState(3);

  // 1-second interval ticker for live countdown
  useEffect(() => {
    const timer = setInterval(() => {
      setNowTimestamp(Date.now());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch Event Metadata & Verify Secure Join Access
  const evaluateAccess = useCallback(async () => {
    if (!ID) {
      setStatus("error");
      setErrorMessage("Invalid live session ID.");
      setLoading(false);
      return;
    }

    try {
      // 1. Fetch public event info first
      const event = await getLiveEventById(ID);
      if (event) {
        setEventData(event);
      }

      // 2. Fetch secure join URL
      const joinRes = await getSecureJoinUrl(ID);
      setJoinData(joinRes);

      const targetDate = event?.event_date || joinRes?.event_date;
      const duration = event?.duration_minutes || joinRes?.duration_minutes || 30;

      if (targetDate) {
        const d = parseUtcDate(targetDate);
        const startTime = d.getTime();
        const openTime = startTime - 60 * 60 * 1000;
        const endTime = startTime + (duration + 15) * 60 * 1000;
        const now = Date.now();

        if (now < openTime) {
          setStatus("too_early");
          setErrorMessage(
            "This live session link opens 1 hour before the scheduled start time."
          );
          setLoading(false);
          return;
        } else if (now > endTime) {
          setStatus("ended");
          setErrorMessage("This live session has concluded.");
          setLoading(false);
          return;
        }
      }

      // If we are within the join window:
      if (joinRes.success && joinRes.stream_link) {
        setStatus("redirecting");
      } else if (joinRes.code === "TOO_EARLY") {
        setStatus("too_early");
        setErrorMessage(
          joinRes.error ||
            "This live session link opens 1 hour before the scheduled start time."
        );
      } else if (joinRes.code === "NOT_REGISTERED" || (event && !event.is_registered)) {
        setStatus("unauthorized");
        setErrorMessage(
          joinRes.error || "You are not currently registered for this session."
        );
      } else if (joinRes.code === "AUTH_REQUIRED") {
        setStatus("auth_required");
        setErrorMessage("Please sign in to access your registered live session.");
      } else {
        setStatus("error");
        setErrorMessage(
          joinRes.error || "Unable to verify session access at this time."
        );
      }
    } catch (err) {
      console.error("Join evaluation error:", err);
      setStatus("error");
      setErrorMessage("Unable to connect to the live session gateway.");
    } finally {
      setLoading(false);
    }
  }, [ID]);

  useEffect(() => {
    evaluateAccess();
  }, [evaluateAccess]);

  // Window check computation for the live countdown
  const timeWindow = useMemo(() => {
    const rawDate = eventData?.event_date || joinData?.event_date;
    const duration = eventData?.duration_minutes || joinData?.duration_minutes || 30;
    if (!rawDate) return null;

    const d = parseUtcDate(rawDate);
    if (!d || isNaN(d.getTime())) return null;

    const startTime = d.getTime();
    const openTime = startTime - 60 * 60 * 1000;
    const endTime = startTime + (duration + 15) * 60 * 1000;

    const diffMs = openTime - nowTimestamp;
    const isEarly = diffMs > 0;
    const isEnded = nowTimestamp > endTime;
    const isOpen = !isEarly && !isEnded;

    let timeStr = "";
    if (isEarly) {
      const totalSecs = Math.max(0, Math.floor(diffMs / 1000));
      const days = Math.floor(totalSecs / 86400);
      const hours = Math.floor((totalSecs % 86400) / 3600);
      const mins = Math.floor((totalSecs % 3600) / 60);
      const secs = totalSecs % 60;

      if (days > 0) {
        timeStr = `${days}d ${hours}h ${mins}m`;
      } else if (hours > 0) {
        timeStr = `${hours}h ${mins}m ${secs < 10 ? "0" : ""}${secs}s`;
      } else {
        timeStr = `${mins}m ${secs < 10 ? "0" : ""}${secs}s`;
      }
    }

    return {
      openTime: new Date(openTime),
      isEarly,
      isOpen,
      isEnded,
      timeStr,
    };
  }, [eventData, joinData, nowTimestamp]);

  // Auto transition when early countdown reaches zero
  useEffect(() => {
    if (status === "too_early" && timeWindow?.isOpen) {
      evaluateAccess();
    }
  }, [status, timeWindow, evaluateAccess]);

  // Auto redirect countdown when access is verified
  useEffect(() => {
    if (status === "redirecting" && joinData?.stream_link) {
      const timer = setInterval(() => {
        setRedirectCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            window.location.href = joinData.stream_link;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [status, joinData]);

  const dualTimes = useMemo(() => {
    const rawDate = eventData?.event_date || joinData?.event_date;
    const duration = eventData?.duration_minutes || joinData?.duration_minutes || 30;
    return formatDualTime(rawDate, duration);
  }, [eventData, joinData]);

  const title = eventData?.title || joinData?.title || "Live Group Guidance";
  const presenter = eventData?.presenter || joinData?.presenter || "MindSherpa Guide";

  return (
    <div className="LiveJoinContainer">
      <div className="sidebarLive">
        <SidebarCol />
      </div>

      <div className="LiveJoinContent">
        {loading && (
          <div className="LiveJoinCard">
            <div className="LiveJoinSpinner"></div>
            <h2 className="LiveJoinTitle">Verifying Session Access</h2>
            <p className="LiveJoinSubtitle">
              Checking your registration status with MindSherpa...
            </p>
          </div>
        )}

        {!loading && status === "redirecting" && joinData?.stream_link && (
          <div className="LiveJoinCard">
            <div className="LiveJoinIconWrapper redirecting">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <h2 className="LiveJoinTitle">Access Verified!</h2>
            <p className="LiveJoinSubtitle">
              Connecting you to <strong>{title}</strong>
            </p>

            {joinData.minutes_until_start > 0 && (
              <div
                style={{
                  backgroundColor: "#EFF6FF",
                  border: "1px solid #BFDBFE",
                  borderRadius: "8px",
                  padding: "10px 16px",
                  margin: "12px 0",
                  color: "#1E40AF",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                ⏱️ Meeting will start in <strong>{joinData.minutes_until_start} min</strong>. Connecting you now to settle in.
              </div>
            )}

            <div className="LiveJoinRedirectBadge">
              Redirecting to live stream in <strong>{redirectCountdown}s</strong>...
            </div>

            <div className="LiveJoinNotice">
              If your browser does not open the meeting automatically, click
              the button below:
            </div>

            <div className="LiveJoinActions">
              <a
                href={joinData.stream_link}
                className="LiveJoinBtnPrimary"
                target="_blank"
                rel="noreferrer"
              >
                <FontAwesomeIcon icon={faVideo} style={{ marginRight: 8 }} />
                Launch Live Stream
              </a>
              <button
                className="LiveJoinBtnSecondary"
                onClick={() => navigate("/live")}
              >
                Return to Live Dashboard
              </button>
            </div>
          </div>
        )}

        {!loading && status === "too_early" && (
          <div className="LiveJoinCard">
            <div className="LiveJoinIconWrapper early">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <h2 className="LiveJoinTitle">Session Not Open Yet</h2>
            <p className="LiveJoinSubtitle">
              The live stream opens <strong>1 hour before</strong> the scheduled start time.
            </p>

            {timeWindow?.timeStr && (
              <div className="LiveJoinCountdownBox">
                <span className="CountdownLabel">Meeting Link Opens In:</span>
                <span className="CountdownValue">{timeWindow.timeStr}</span>
              </div>
            )}

            <div className="LiveJoinSessionInfoBox">
              <div className="SessionInfoRow">
                <FontAwesomeIcon icon={faVideo} className="SessionInfoIcon" />
                <span className="SessionInfoText"><strong>{title}</strong></span>
              </div>
              <div className="SessionInfoRow">
                <FontAwesomeIcon icon={faUser} className="SessionInfoIcon" />
                <span className="SessionInfoText">Guide: {presenter}</span>
              </div>

              {dualTimes.localDateStr && (
                <div className="SessionTimeBlock">
                  <div className="LocalTimeRow">
                    <span className="TimeBadge">Your Local Time</span>
                    <span className="TimeValue">
                      {dualTimes.localDateStr} at {dualTimes.localTimeStr}
                    </span>
                  </div>
                  {dualTimes.isDifferentTz && (
                    <div className="HostTimeRow">
                      <span className="TimeBadgeHost">🇨🇦 Canada (EST)</span>
                      <span className="TimeValueHost">
                        {dualTimes.hostDateStr} at {dualTimes.hostTimeStr}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="LiveJoinNotice">
              <FontAwesomeIcon icon={faShieldHalved} style={{ marginRight: 6, color: "#094B38" }} />
              <strong>Confirmed Reservation:</strong> You are registered for this session. Keep this tab open or return when the countdown reaches zero — the meeting link will activate automatically!
            </div>

            <div className="LiveJoinActions">
              <button
                className="LiveJoinBtnPrimary"
                onClick={() =>
                  navigate(
                    ID ? `/live/register/session/${ID}` : "/live"
                  )
                }
              >
                <FontAwesomeIcon icon={faArrowLeft} style={{ marginRight: 8 }} />
                View Session Details
              </button>
              <button
                className="LiveJoinBtnSecondary"
                onClick={() => navigate("/live")}
              >
                Live Sessions Dashboard
              </button>
            </div>
          </div>
        )}

        {!loading && status === "unauthorized" && (
          <div className="LiveJoinCard">
            <div className="LiveJoinIconWrapper unauthorized">
              <FontAwesomeIcon icon={faLock} />
            </div>
            <h2 className="LiveJoinTitle">Registration Required</h2>
            <p className="LiveJoinSubtitle">{errorMessage}</p>
            <div className="LiveJoinNotice">
              Access to MindSherpa Live Guidance sessions is reserved for registered attendees. If you have not registered yet or previously cancelled your reservation, please register below to join.
            </div>
            <div className="LiveJoinActions">
              <button
                className="LiveJoinBtnPrimary"
                onClick={() =>
                  navigate(
                    ID ? `/live/register/session/${ID}` : "/live"
                  )
                }
              >
                Register for this Session
              </button>
              <button
                className="LiveJoinBtnSecondary"
                onClick={() => navigate("/live")}
              >
                Browse Upcoming Sessions
              </button>
            </div>
          </div>
        )}

        {!loading && status === "auth_required" && (
          <div className="LiveJoinCard">
            <div className="LiveJoinIconWrapper unauthorized">
              <FontAwesomeIcon icon={faLock} />
            </div>
            <h2 className="LiveJoinTitle">Sign In Required</h2>
            <p className="LiveJoinSubtitle">
              Please sign in to your MindSherpa account to verify your live session reservation.
            </p>
            <div className="LiveJoinActions">
              <button
                className="LiveJoinBtnPrimary"
                onClick={() => navigate(`/login?redirect=/live/join/${ID}`)}
              >
                Sign In to MindSherpa
              </button>
              <button
                className="LiveJoinBtnSecondary"
                onClick={() => navigate("/live")}
              >
                Return to Live Dashboard
              </button>
            </div>
          </div>
        )}

        {!loading && status === "ended" && (
          <div className="LiveJoinCard">
            <div className="LiveJoinIconWrapper ended">
              <FontAwesomeIcon icon={faClock} />
            </div>
            <h2 className="LiveJoinTitle">Session Concluded</h2>
            <p className="LiveJoinSubtitle">
              This live guidance session has already ended.
            </p>
            <div className="LiveJoinNotice">
              Thank you for participating! You can explore other upcoming live group guidance sessions or view your past session recordings in your dashboard.
            </div>
            <div className="LiveJoinActions">
              <button
                className="LiveJoinBtnPrimary"
                onClick={() => navigate("/live")}
              >
                Explore Upcoming Sessions
              </button>
            </div>
          </div>
        )}

        {!loading && status === "error" && (
          <div className="LiveJoinCard">
            <div className="LiveJoinIconWrapper unauthorized">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <h2 className="LiveJoinTitle">Unable to Connect</h2>
            <p className="LiveJoinSubtitle">{errorMessage}</p>
            <div className="LiveJoinActions">
              <button
                className="LiveJoinBtnPrimary"
                onClick={() => navigate("/live")}
              >
                Return to Live Dashboard
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveJoinRedirect;
